import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Render simple markdown-ish briefing text (paragraphs + line breaks).
 * @param {string} text
 */
function BriefingMarkdown({ text }) {
  if (!text) return null;

  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());

  return (
    <Box>
      {paragraphs.map((paragraph, index) => (
        <Typography
          key={index}
          variant="body1"
          paragraph
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
        >
          {paragraph.trim()}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * AI operations drawer — Gemini-powered daily ops briefing.
 * @param {{ open: boolean, onClose: () => void, userData: Record<string, unknown> | null }} props
 */
export default function AIOperationsCenter({ open, onClose, userData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [briefing, setBriefing] = useState('');

  const loadBriefing = useCallback(async () => {
    if (!userData?.companyId) {
      setError('You need a company assignment to generate a briefing.');
      setBriefing('');
      return;
    }

    setLoading(true);
    setError('');
    setBriefing('');

    try {
      const fn = httpsCallable(functions, 'generateOpsBriefing');
      const result = await fn();
      const data = result.data || {};
      if (typeof data.briefing === 'string' && data.briefing.trim()) {
        setBriefing(data.briefing.trim());
      } else {
        setError('No briefing was returned. Try again in a moment.');
      }
    } catch (e) {
      console.error('AIOperationsCenter:', e);
      const message =
        e?.message ||
        e?.details ||
        'Could not generate the operations briefing.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [userData?.companyId]);

  useEffect(() => {
    if (!open) return;
    loadBriefing();
  }, [open, loadBriefing]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420, md: 480 },
          p: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography variant="h6">AI Operations Center</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close AI operations drawer">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Daily briefing powered by Gemini — sprint health, ticket load, and recommended actions.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Analyzing sprint and ticket data…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && briefing && <BriefingMarkdown text={briefing} />}

        {!loading && !error && !briefing && (
          <Typography variant="body2" color="text.secondary">
            No briefing available yet.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
